import { DomainInitPayload } from '@sxm-de/dev-kits/web-sdk/types';
import {
    getAutomotiveSubscriptionStatusFromLifecycleState,
    getSubscriptionAdditionalData,
    getSubscriptionsAll,
} from '@sxm-de/dev-kits/web-sdk/domains/subscriptions-core';

export type CheckIdentityEligibilityForAutomotiveSubscriptionFn =
    () => Promise<void>;

export function initCheckIdentityEligibilityForAutomotiveSubscriptionFn({
    dependencies,
}: DomainInitPayload): CheckIdentityEligibilityForAutomotiveSubscriptionFn {
    return async () => {
        const { appEventSdk } = dependencies;

        const subscriptionsFromEndpoint =
            await getSubscriptionsAll(dependencies);

        const subscriptions = subscriptionsFromEndpoint.filter(
            (subscription) => !subscription.source.sms,
        );

        const activeSubscriptions = subscriptions.some((subscription) =>
            ['active', 'pending_cancellation'].some(
                (state) =>
                    state.toLowerCase() === subscription.state.toLowerCase(),
            ),
        );

        if (activeSubscriptions) {
            appEventSdk.trackEvent({
                event: 'customer-info',
                data: {
                    subscriptions: subscriptions.map((subscription) => ({
                        id: subscription?.id,
                        planCode: subscription?.products?.[0]?.name,
                        termLength:
                            subscription?.currentPhase?.billingPeriod?.months
                                ?.count,
                        status: subscription?.state,
                        startDate: subscription?.started,
                        terminatedDate: subscription?.terminated,
                        offerId: subscription?.offerId,
                        currentPhaseType: subscription?.currentPhase?.type,
                        billingSource: Object.keys(subscription?.source)?.[0],
                    })),
                },
            });

            // get automotive subscription info for each subscription
            for (let i = 0; i < subscriptions.length; i++) {
                const subscription = subscriptions[i];
                const { detailedState } = await getSubscriptionAdditionalData(
                    {
                        subscription,
                    },
                    dependencies,
                );
                subscription['detailedState'] = detailedState;
            }

            const automotiveSubscription = subscriptions.find(
                (sub) => sub.detailedState,
            );

            // identity does not have automotive subscription, only streaming
            if (!automotiveSubscription) {
                throw new Error('IDENTITY_NOT_ELIGIBLE_ERROR');
            }

            const existingAutomotiveSubscriptionStatus =
                automotiveSubscription.detailedState &&
                getAutomotiveSubscriptionStatusFromLifecycleState(
                    automotiveSubscription.detailedState.lifecycleState,
                );

            switch (existingAutomotiveSubscriptionStatus) {
                case 'SELF_PAY':
                case 'TRIAL_WITH_FOLLOW_ON':
                    throw new Error('EXISTING_SELF_PAY_SUBSCRIPTION_ERROR');
            }
        }
    };
}
